import { useCallback, useState } from 'react';
import { REQUIRED_FIELDS_MESSAGE, formatMissingFieldsMessage } from '../utils/formValidation';

export { REQUIRED_FIELDS_MESSAGE };

export type FormFieldErrors = {
  message: string | null;
  invalid: (key: string) => boolean;
  show: (message: string, fieldKeys?: string[]) => void;
  showMissing: (fieldKeys: string[], labels?: string[]) => void;
  clearField: (key: string) => void;
  clear: () => void;
};

export function useFormFieldErrors(): FormFieldErrors {
  const [message, setMessage] = useState<string | null>(null);
  const [fields, setFields] = useState<Set<string>>(new Set());

  const show = useCallback((nextMessage: string, fieldKeys: string[] = []) => {
    setMessage(nextMessage);
    setFields(new Set(fieldKeys));
  }, []);

  const showMissing = useCallback((fieldKeys: string[], labels?: string[]) => {
    const names = (labels ?? []).map((label) => label.trim()).filter(Boolean);
    show(
      names.length > 0 ? formatMissingFieldsMessage(names) : REQUIRED_FIELDS_MESSAGE,
      fieldKeys,
    );
  }, [show]);

  const clear = useCallback(() => {
    setMessage(null);
    setFields(new Set());
  }, []);

  const clearField = useCallback((key: string) => {
    setFields((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      if (next.size === 0) {
        setMessage(null);
      }
      return next;
    });
  }, []);

  const invalid = useCallback((key: string) => fields.has(key), [fields]);

  return { message, invalid, show, showMissing, clearField, clear };
}
